import React, { useState, useEffect } from "react";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import SearchInput from "@/components/SearchInput/SearchInput";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CourseListFilters } from "@/modules/learningCenter/features/CourseManagement/hooks/useCourseList/useCourseList";
import { CourseMetadata } from "@/modules/learningCenter/features/CourseManagement/types/course";

interface CourseFiltersProps {
  filters: CourseListFilters;
  onFilterChange: (filterKey: keyof CourseListFilters, value: string) => void;
  metadata: CourseMetadata | null;
}

const ALL_VALUE = "__all__";

const CourseFilters: React.FC<CourseFiltersProps> = ({
  filters,
  onFilterChange,
  metadata,
}) => {
  const [searchInput, setSearchInput] = useState(filters.search);
  const debouncedSearch = useDebouncedValue(searchInput, 500);

  useEffect(() => {
    if (debouncedSearch !== filters.search) {
      onFilterChange("search", debouncedSearch);
    }
  }, [debouncedSearch, filters.search, onFilterChange]);

  const statuses = metadata?.statuses ?? [];

  return (
    <div className="mb-4 space-y-4">
      <SearchInput
        onChange={(val) => setSearchInput(val)}
        placeholder="Search courses by title..."
        value={searchInput}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className="mb-1 block text-sm text-gray-500 dark:text-gray-400">
            Status
          </label>
          <Select
            onValueChange={(val) =>
              onFilterChange("status", val === ALL_VALUE ? "" : val)
            }
            value={filters.status || ALL_VALUE}
          >
            <SelectTrigger aria-label="Filter by status">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>All statuses</SelectItem>
              {statuses.map((status) => (
                <SelectItem key={status.value} value={status.value}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default CourseFilters;
